import React, { useState } from "react";
import axios from "axios";
import { API } from "../App";

export default function AffiliatePage() {
  const [form, setForm] = useState({
    name: "", email: "", type: "accountant",
    bank_bsb: "", bank_account: "", why: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.why) {
      setError("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/affiliates/register`, form);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const S = {
    page: { minHeight: "100vh", background: "#F9FAFB", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" },
    card: { background: "#fff", borderRadius: 20, boxShadow: "0 4px 32px rgba(0,0,0,0.08)", padding: "40px 36px", width: "100%", maxWidth: 520 },
    h1: { fontSize: 26, fontWeight: 800, color: "#111827", margin: "0 0 6px" },
    sub: { fontSize: 14, color: "#6B7280", margin: "0 0 28px", lineHeight: 1.5 },
    label: { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 },
    input: { width: "100%", boxSizing: "border-box", border: "1px solid #E5E7EB", borderRadius: 10, padding: "10px 14px", fontSize: 14, outline: "none", fontFamily: "inherit", background: "#FAFAFA" },
    textarea: { width: "100%", boxSizing: "border-box", border: "1px solid #E5E7EB", borderRadius: 10, padding: "10px 14px", fontSize: 14, outline: "none", fontFamily: "inherit", background: "#FAFAFA", resize: "vertical" },
    select: { width: "100%", boxSizing: "border-box", border: "1px solid #E5E7EB", borderRadius: 10, padding: "10px 14px", fontSize: 14, outline: "none", fontFamily: "inherit", background: "#FAFAFA", appearance: "none" },
    row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
    btn: { width: "100%", padding: 14, background: "#1A4D2E", color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", marginTop: 8 },
    err: { background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#DC2626", marginBottom: 16 },
    field: { marginBottom: 16 },
    pill: (v, val) => ({
      flex: 1, padding: "10px 14px", borderRadius: 10, border: v === val ? "2px solid #1A4D2E" : "1px solid #E5E7EB",
      background: v === val ? "#F0FDF4" : "#FAFAFA", cursor: "pointer", textAlign: "center",
      fontSize: 14, fontWeight: v === val ? 700 : 400, color: v === val ? "#1A4D2E" : "#374151",
    }),
  };

  if (done) return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", marginBottom: 8 }}>Application received!</h2>
          <p style={{ fontSize: 15, color: "#6B7280", lineHeight: 1.6 }}>
            Thanks for applying to the TaxBuddy affiliate program.<br />
            We'll be in touch within <strong>48 hours</strong>.
          </p>
          <a href="/login" style={{ display: "inline-block", marginTop: 24, padding: "12px 28px", background: "#1A4D2E", color: "#fff", borderRadius: 10, textDecoration: "none", fontWeight: 700, fontSize: 14 }}>
            Back to TaxBuddy
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={S.card}>
        <h1 style={S.h1}>Become a TaxBuddy Partner</h1>
        <p style={S.sub}>
          Earn recurring commissions by referring clients to TaxBuddy. Accountants earn $2/month per active subscriber. Influencers earn $1.50/month. Your referrals get an exclusive discount.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={S.field}>
            <label style={S.label}>Partner type</label>
            <div style={{ display: "flex", gap: 8 }}>
              {["accountant", "influencer"].map(t => (
                <div key={t} style={S.pill(form.type, t)} onClick={() => setForm(f => ({ ...f, type: t }))}>
                  {t === "accountant" ? "Accountant / Bookkeeper" : "Influencer / Creator"}
                </div>
              ))}
            </div>
          </div>

          <div style={S.field}>
            <label style={S.label}>Full name *</label>
            <input style={S.input} value={form.name} onChange={set("name")} placeholder="Your name" required />
          </div>

          <div style={S.field}>
            <label style={S.label}>Email address *</label>
            <input style={S.input} type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required />
          </div>

          <div style={{ ...S.field, ...S.row }}>
            <div>
              <label style={S.label}>Bank BSB</label>
              <input style={S.input} value={form.bank_bsb} onChange={set("bank_bsb")} placeholder="062000" />
            </div>
            <div>
              <label style={S.label}>Account number</label>
              <input style={S.input} value={form.bank_account} onChange={set("bank_account")} placeholder="12345678" />
            </div>
          </div>

          <div style={S.field}>
            <label style={S.label}>Why do you want to partner with TaxBuddy? *</label>
            <textarea
              style={S.textarea} rows={4}
              value={form.why} onChange={set("why")}
              placeholder="Tell us about your audience or client base, and how you'd promote TaxBuddy…"
              required
            />
          </div>

          {error && <div style={S.err}>{error}</div>}

          <button type="submit" style={{ ...S.btn, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
            {submitting ? "Submitting…" : "Submit application"}
          </button>
        </form>

        <p style={{ fontSize: 12, color: "#9CA3AF", textAlign: "center", marginTop: 16 }}>
          Already a partner?{" "}
          <a href="/affiliate/dashboard" style={{ color: "#1A4D2E", fontWeight: 600, textDecoration: "none" }}>
            View your dashboard →
          </a>
        </p>
      </div>
    </div>
  );
}
